import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { logger } from '../utils/logger';
import { getPracticeId } from '../utils/practice';
import { queueConfirmedAnalyticsEvent } from '../utils/ga4';

/**
 * Role / scope surface from /api/v1/auth/me (ADR-0004).
 *
 * `role` is the lowercased UserRole enum value (super_admin,
 * chain_executive, regional_manager, store_manager, clinical,
 * dispensing, staff, patient). Legacy users predating migration 023
 * receive `null` — UI gating treats null as "no access".
 *
 * `scope` carries the four scope FKs the backend's `apply_scope()`
 * helper uses to filter list/detail queries. Frontend reads these to
 * pre-fill filters and resolve the caller's corporation_id without an
 * extra round trip.
 */
export interface UserScope {
  corporation_id: string | null;
  region_id: string | null;
  store_id: string | null;
  practice_id: string | null;
}

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  practice_id?: string;
  mfa_enabled?: boolean;
  // ADR-0004: role + scope surface for UI gating.
  role?: string | null;
  scope?: UserScope | null;
}

interface MFASetupResponse {
  secret: string;
  qr_code: string;
  backup_codes: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaRequired: boolean;
  login: (email: string, password: string) => Promise<void>;
  verifyMFA: (code: string) => Promise<void>;
  setupMFA: () => Promise<MFASetupResponse>;
  enableMFA: (code: string, secret: string) => Promise<void>;
  disableMFA: () => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempAuthData, setTempAuthData] = useState<{ email: string; password: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          logger.error('Authentication error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token]);

  // Mirror the user's practice_id into localStorage so module-level
  // services (loyaltyService.getDefaultPracticeId, etc.) can resolve
  // the current practice without re-reading the React context.
  //
  // Source of truth: prefer `user.scope.practice_id` (ADR-0004 — the
  // canonical scope surface from /auth/me). Fall back to the legacy
  // top-level `user.practice_id` so older payload shapes still work.
  // Without this mirror the loyalty pages send the literal string
  // "default-practice" and every /api/v1/loyalty/admin/* request 400s
  // on the UUID coercion (per the Claude-Code-web bug list).
  useEffect(() => {
    const practiceId = getPracticeId(user);
    if (practiceId) {
      localStorage.setItem('practiceId', practiceId);
    } else {
      localStorage.removeItem('practiceId');
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      logger.log('Attempting login with API baseURL:', api.defaults.baseURL);

      const loginEndpoint = '/auth/login';
      logger.log('Using login endpoint:', loginEndpoint);

      // Send POST request with form data (OAuth2 expects username field)
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await api.post(loginEndpoint, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        logger.log('MFA required for this account');
        setMfaRequired(true);
        setTempAuthData({ email, password });
        // Don't navigate yet, wait for MFA verification
        return;
      }

      logger.log('Login successful, token received');
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Fetch user data
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      const userEndpoint = '/auth/me';
      logger.log('Using user info endpoint:', userEndpoint);
      
      const userResponse = await api.get(userEndpoint);
      setUser(userResponse.data);
      queueConfirmedAnalyticsEvent('first_login', { method: 'email' });
      
      navigate('/app');
    } catch (error: unknown) {
      logger.error('Login error:', error);

      // Improved error handling
      let errorMessage = 'Login failed. Please try again.';

      if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = error as { response?: { status?: number; data?: unknown } };
        logger.error('Server response error:', errorResponse.response?.status, errorResponse.response?.data);

        if (errorResponse.response?.status === 401) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (errorResponse.response?.status === 422) {
          errorMessage = 'Invalid request format. Please contact support.';
        } else if (errorResponse.response?.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (errorResponse.response?.data && typeof errorResponse.response.data === 'object' && 'detail' in errorResponse.response.data) {
          errorMessage = String(errorResponse.response.data.detail);
        }
      } else if (error && typeof error === 'object' && 'request' in error) {
        logger.error('No response received:', (error as { request: unknown }).request);
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      // Throw a more informative error
      const enhancedError = new Error(errorMessage);
      Object.assign(enhancedError, { originalError: error });
      throw enhancedError;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, firstName?: string, lastName?: string) => {
    setIsLoading(true);
    try {
      await api.post('/auth/register', {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      queueConfirmedAnalyticsEvent('sign_up', { method: 'email' });
      
      // Auto login after registration
      await login(email, password);
    } catch (error) {
      logger.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all possible token keys used across the app
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('vf_token');
    localStorage.removeItem('vf_temp_token');
    localStorage.removeItem('impersonation_token');
    setToken(null);
    setUser(null);
    setMfaRequired(false);
    setTempAuthData(null);
    api.defaults.headers.common['Authorization'] = '';
    navigate('/login');
  };
  
  const verifyMFA = async (code: string) => {
    if (!tempAuthData) {
      throw new Error('No authentication data available');
    }
    
    setIsLoading(true);
    try {
      const response = await api.post('/auth/mfa/verify', { code });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      const userResponse = await api.get('/user/me');
      setUser(userResponse.data);
      queueConfirmedAnalyticsEvent('first_login', { method: 'email' });
      
      setMfaRequired(false);
      setTempAuthData(null);
      navigate('/app');
    } catch (error) {
      logger.error('MFA verification error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const setupMFA = async (): Promise<MFASetupResponse> => {
    if (!token) {
      throw new Error('Authentication required');
    }
    
    try {
      const response = await api.post('/auth/mfa/setup');
      return response.data;
    } catch (error) {
      logger.error('MFA setup error:', error);
      throw error;
    }
  };
  
  const enableMFA = async (code: string, secret: string) => {
    if (!token) {
      throw new Error('Authentication required');
    }
    
    try {
      await api.post('/auth/mfa/enable', { code, secret });
      // Update user to reflect MFA enabled
      if (user) {
        setUser({ ...user, mfa_enabled: true });
      }
    } catch (error) {
      logger.error('MFA enable error:', error);
      throw error;
    }
  };
  
  const disableMFA = async () => {
    if (!token) {
      throw new Error('Authentication required');
    }
    
    try {
      await api.post('/auth/mfa/disable');
      // Update user to reflect MFA disabled
      if (user) {
        setUser({ ...user, mfa_enabled: false });
      }
    } catch (error) {
      logger.error('MFA disable error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        mfaRequired,
        login,
        verifyMFA,
        setupMFA,
        enableMFA,
        disableMFA,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
